import React, { useRef, useState } from "react";
import { View, Text, TouchableOpacity, StyleSheet } from "react-native";
import { observer } from "mobx-react";
import { Entypo, Ionicons } from "@expo/vector-icons";

import { useDevice } from "../../_layout";
import Colors from "../../../Resources/Color";
import Tooltip from "../../Components/Tooltip";
import FloatingMenu from "../../Components/FloatingMenu";
import HoverableTouchableOpacity from "../../Components/HoverableTouchableOpacity";
import ActivePulseIndicator from "./ActivePulseIndicator";
import {
  resolveBlockCardIsActive,
  resolveBlockCardTimelineState,
} from "./blockCardTimelineUtils";

const getCardWidthStyle = (screenSize) => {
  if (screenSize === "small") return styles.cardSmall;
  if (screenSize === "medium") return styles.cardMedium;
  return styles.cardLarge;
};

const formatBlockInitialDate = (initialDate, language) => {
  if (!initialDate) return "-";
  const parsedDate =
    initialDate instanceof Date ? initialDate : new Date(initialDate);
  if (!Number.isFinite(parsedDate.getTime())) return "-";
  return parsedDate.toLocaleDateString(language === "es" ? "es-ES" : "en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const BlockCardMenu = ({
  menuItems,
  onAction,
  isMobile,
  isOpen,
  onOpenChange,
}) => {
  const [isTriggerHovered, setIsTriggerHovered] = useState(false);

  return (
    <FloatingMenu
      placement="bottom-end"
      width={230}
      items={menuItems}
      open={isOpen}
      onOpenChange={onOpenChange}
      triggerContainerStyle={styles.cardIconButton}
      trigger={
        <View
          style={{
            opacity: isTriggerHovered ? 1 : 0.75,
            transition: "opacity 150ms ease",
          }}
          onMouseEnter={() => !isMobile && setIsTriggerHovered(true)}
          onMouseLeave={() => setIsTriggerHovered(false)}
        >
          <Entypo
            name="dots-three-horizontal"
            size={16}
            color={isTriggerHovered ? Colors.darkgrayColor : Colors.grayColor}
          />
        </View>
      }
      renderItem={({ item, close }) => (
        <HoverableTouchableOpacity
          style={styles.blockMenuItemRow}
          hoverStyle={styles.blockMenuItemHover}
          onPress={(e) => {
            e?.stopPropagation?.();
            onAction(item.key);
            close();
          }}
          title={item.label}
        >
          {item?.icon?.type === "ionicons" ? (
            <Ionicons
              name={item.icon.name}
              size={14}
              color={item.isDanger ? Colors.redColor : Colors.darkgrayColor}
            />
          ) : null}
          <Text
            style={[
              styles.blockMenuItemText,
              item.isDanger ? styles.blockMenuItemDanger : null,
            ]}
          >
            {item.label}
          </Text>
        </HoverableTouchableOpacity>
      )}
    />
  );
};

const BlockCard = observer(
  ({
    block,
    folderKey,
    blockId,
    screenSize,
    i18n,
    t,
    menuItems,
    onPress,
    onSummaryPress,
    onMenuAction,
    showRightIcons = true,
    showSummaryAction = true,
    showInitialDate = true,
    timelineVariant = "status",
    activeBlockId = null,
    isSelected = false,
    disableKeyboardFocus = false,
    dragAttributes,
    dragListeners,
    isDragging = false,
    draggingOpacity = 0.45,
    isDropZoneActive = false,
    dragDisabled = false,
    suppressPress = false,
    dragNodeRef,
    timelineLeadingText = "",
    showActivePulseIndicator = true,
    openMenuOnPress = false,
    touchableStyle = null,
  }) => {
    const { isMobile } = useDevice();
    const [isHovered, setIsHovered] = useState(false);
    const [isSummaryIconHovered, setIsSummaryIconHovered] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const rowPressStartedWithMenuOpenRef = useRef(false);

    const totalWeeks = block?.totalWeeks ?? 0;
    const createdWeeks = block?.getWeeks ?? 0;
    const refDate = new Date();
    const hasDateContext = block?.initialDate != null;
    const isBlockActive = resolveBlockCardIsActive(block, activeBlockId);
    const { timelineCurrentWeek, isBlockFullyCompleted } =
      resolveBlockCardTimelineState({
        block,
        hasDateContext,
        createdWeeks,
        totalWeeks,
        isBlockActive,
        refDate,
      });

    const interactiveDragProps = dragDisabled
      ? {}
      : {
        ...dragAttributes,
        ...dragListeners,
      };

    return (
      <TouchableOpacity
        ref={dragNodeRef}
        testID={`program-folder-${folderKey}-block-${blockId}`}
        style={[
          styles.blockCardTouchable,
          getCardWidthStyle(screenSize),
          isDragging && { opacity: draggingOpacity },
          touchableStyle,
        ]}
        onPressIn={() => {
          if (!openMenuOnPress) return;
          rowPressStartedWithMenuOpenRef.current = Boolean(isMenuOpen);
        }}
        onPress={() => {
          if (suppressPress || isDragging) return;
          if (openMenuOnPress) {
            const startedWithMenuOpen = rowPressStartedWithMenuOpenRef.current;
            rowPressStartedWithMenuOpenRef.current = false;
            setIsMenuOpen(!startedWithMenuOpen);
            return;
          }
          onPress?.(block);
        }}
        activeOpacity={0.92}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        focusable={!disableKeyboardFocus}
        tabIndex={disableKeyboardFocus ? -1 : undefined}
        {...interactiveDragProps}
      >
        <View
          style={[
            styles.blockCard,
            isDropZoneActive && !isDragging ? styles.blockCardDropZoneActive : null,
            isHovered && styles.blockCardHovered,
            isSelected && styles.blockCardSelected,
            { transition: "border-color 200ms ease-in-out" },
          ]}
        >
          <View style={styles.blockCardContent}>
            <View style={styles.blockCardHeader}>
              <View style={styles.blockCardTitleRow}>
                <View style={styles.blockCardTitleWrap}>
                  {isBlockActive && showActivePulseIndicator && <ActivePulseIndicator />}
                  <Text numberOfLines={1} style={styles.blockCardTitle}>
                    {block?.name}
                  </Text>
                </View>
              </View>

              {showRightIcons ? (
                <View style={styles.blockCardActions}>
                  {showSummaryAction &&
                    (block?.protocolsAndExercises?.length ?? 0) > 0 && (
                    <Tooltip content="openBlockSummary-string" placement="top">
                      <TouchableOpacity
                        style={[
                          styles.cardIconButton,
                          {
                            opacity: isSummaryIconHovered ? 1 : 0.75,
                            transition: "opacity 150ms ease",
                          },
                        ]}
                        onPress={(e) => {
                          e?.stopPropagation?.();
                          onSummaryPress?.(block);
                        }}
                        onMouseEnter={() => !isMobile && setIsSummaryIconHovered(true)}
                        onMouseLeave={() => setIsSummaryIconHovered(false)}
                        activeOpacity={0.8}
                      >
                        <Ionicons
                          name="document-text-outline"
                          size={15}
                          color={isSummaryIconHovered ? Colors.darkgrayColor : Colors.grayColor}
                        />
                      </TouchableOpacity>
                    </Tooltip>
                    )}

                  <BlockCardMenu
                    menuItems={menuItems}
                    onAction={(itemKey) => onMenuAction(block, itemKey)}
                    isMobile={isMobile}
                    isOpen={isMenuOpen}
                    onOpenChange={setIsMenuOpen}
                  />
                </View>
              ) : null}
            </View>

            {totalWeeks > 0 && (
              <View style={styles.timelineBarContainer}>
                <View style={styles.timelineDateRow}>
                  {timelineLeadingText ? (
                    <Text numberOfLines={1} style={styles.timelineLeadingLabel}>
                      {timelineLeadingText}
                    </Text>
                  ) : showInitialDate && block?.initialDate ? (
                    <Tooltip
                      content={t("startingWeekOf-string", {
                        date: formatBlockInitialDate(block.initialDate, i18n.language),
                      })}
                      placement="top"
                      delayedOpen
                      rawText
                    >
                      <Text numberOfLines={1} style={styles.timelineDateLabel}>
                        {formatBlockInitialDate(block.initialDate, i18n.language)}
                      </Text>
                    </Tooltip>
                  ) : (
                    <View style={styles.timelineDateSpacer} />
                  )}
                  {!isBlockFullyCompleted && (
                    <Text numberOfLines={1} style={styles.weeksPlannedText}>
                      {t("weeksPlannedOf-string", {
                        created: createdWeeks,
                        total: totalWeeks,
                      })}
                    </Text>
                  )}
                </View>
                <View style={styles.timelineBarRow}>
                  <View style={styles.timelineBar}>
                    {[...Array(totalWeeks)].map((_, index) => {
                      const weekNumber = index + 1;
                      const isCreated = weekNumber <= createdWeeks;
                      const isCompletedOrCurrent =
                        weekNumber <= timelineCurrentWeek;
                      const isMissing = !isCreated && isCompletedOrCurrent;

                      const segmentStyle = timelineVariant === "neutral"
                        ? styles.timelineSegmentNotCreated
                        : !hasDateContext
                        ? styles.timelineSegmentNotCreated
                        : isCreated && isCompletedOrCurrent
                          ? styles.timelineSegmentCompleted
                          : isCreated && !isCompletedOrCurrent
                            ? styles.timelineSegmentPending
                            : !isCreated && !isMissing
                              ? styles.timelineSegmentNotCreated
                              : styles.timelineSegmentMissing;

                      return (
                        <View
                          key={index}
                          style={[styles.timelineSegment, segmentStyle]}
                        />
                      );
                    })}
                  </View>
                </View>
              </View>
            )}
          </View>
        </View>
      </TouchableOpacity>
    );
  }
);

const styles = StyleSheet.create({
  blockCardTouchable: {
    borderRadius: 12,
    overflow: "hidden",
    outlineStyle: "none",
    outlineWidth: 0,
    outlineColor: "transparent",
    boxShadow: "none",
  },
  blockCard: {
    borderWidth: 0.1,
    borderColor: Colors.borderColor,
    borderRadius: 12,
    backgroundColor: Colors.whiteColor,
    overflow: "hidden",
    transitionProperty: "border-color",
    transitionDuration: "200ms",
    transitionTimingFunction: "ease-in-out",
  },
  blockCardDropZoneActive: {
    opacity: 0.55,
  },
  blockCardHovered: {
    borderColor: Colors.grayColor,
  },
  blockCardSelected: {
    borderColor: Colors.bubbleColor,
    backgroundColor: "rgba(63, 123, 141, 0.07)",
  },
  cardSmall: {
    width: "100%",
  },
  cardMedium: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: "calc((100% - 12px) / 2)",
  },
  cardLarge: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: "calc((100% - 36px) / 4)",
  },
  blockCardContent: {
    flex: 1,
    paddingTop: 12,
    paddingBottom: 14,
    paddingHorizontal: 14,
    gap: 6,
    position: "relative",
  },
  blockCardHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  blockCardTitleRow: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
  },
  blockCardTitleWrap: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    minWidth: 0,
  },
  blockCardTitle: {
    flex: 1,
    minWidth: 0,
    fontSize: 14,
    fontWeight: "500",
    lineHeight: 20,
    color: Colors.blackColor,
  },
  blockCardActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  timelineBarContainer: {
    width: "100%",
    marginTop: 0,
    marginBottom: 2,
    gap: 4,
  },
  timelineDateRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: 14,
    gap: 8,
  },
  timelineDateSpacer: {
    flex: 1,
  },
  timelineDateLabel: {
    flexShrink: 0,
    fontSize: 10,
    lineHeight: 14,
    color: Colors.grayColor,
  },
  timelineLeadingLabel: {
    flexShrink: 1,
    minWidth: 0,
    fontSize: 10,
    lineHeight: 14,
    color: Colors.grayColor,
  },
  timelineBarRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  timelineBar: {
    flex: 1,
    flexDirection: "row",
    height: 5,
    minWidth: 0,
    borderRadius: 8,
    overflow: "hidden",
    gap: 2,
  },
  timelineSegment: {
    height: "100%",
    borderRadius: 4,
    flex: 1,
  },
  timelineSegmentCompleted: {
    backgroundColor: Colors.bubbleColor,
  },
  timelineSegmentPending: {
    backgroundColor: "#D1D5DB",
  },
  timelineSegmentNotCreated: {
    backgroundColor: "#D1D5DB",
  },
  timelineSegmentMissing: {
    backgroundColor: "#C75450",
  },
  weeksPlannedText: {
    flexShrink: 1,
    minWidth: 0,
    fontSize: 10,
    color: Colors.grayColor,
    lineHeight: 14,
  },
  cardIconButton: {
    width: 28,
    height: 28,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  blockMenuItemRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderRadius: 8,
  },
  blockMenuItemHover: {
    backgroundColor: Colors.hoveredGray,
  },
  blockMenuItemText: {
    fontSize: 12,
    color: Colors.blackColor,
    fontWeight: "500",
  },
  blockMenuItemDanger: {
    color: Colors.redColor,
  },
});

export default BlockCard;
